/**
 * useChat — chat feature orchestration hook.
 *
 * Owns the message list, pagination cursor, live listener lifecycle, E2EE
 * key derivation per peer, optimistic-insert + send-error UX, retry-on-
 * reconnect, presence (Redux `auth.chattingWith` mirror), tap-to-toggle
 * timestamp map, read-receipt position memo, read-on-scroll-bottom triggers,
 * scrolled-away unread tracking, AND deep-link rehydration of
 * `ChatUsersInfoEntity` (the URL-only path where `location.state` is absent —
 * fetches the peer user, derives `groupChatId`, builds the entity). The
 * no-mutual-subscription CTA is owned by the page (`ChatPage`) — it has
 * `screenKey` and forwards the hook's `peerUser` into `SubscribeUserSheet.show`.
 * The hook intentionally does NOT call sheets / UI surfaces.
 *
 * Page lifecycle ownership (Finding 31): the hook does NOT call `useBasePage`
 * — that hook is page-only. Instead it exposes `onPageInit` /
 * `onPageVisible` / `onPageHidden` / `onPageDispose` as memoized callbacks
 * which the host page wires into its own `useBasePage` call. The hook still
 * owns every state mutation those lifecycle phases trigger (initial load,
 * presence claim/release, live-listener teardown) — only the lifecycle
 * registration moved out.
 *
 * Flutter source of truth: `chat_screen.dart:177-717` (full state-management
 * block). Per the plan (Task 16) the web port mirrors the responsibilities
 * but stays within web conventions:
 *  - VirtualList (reversed) for the list; pagination triggers via
 *    `onReachedEnd` (caller wires it through `onLoadMore`).
 *  - `useBasePage` (page-owned) for route-visibility transitions (Flutter
 *    `didPopNext`/`didPushNext`/`dispose`) — the page forwards every event
 *    into the lifecycle callbacks this hook exposes.
 *  - `useTabVisibility` for tab-visibility transitions (Flutter
 *    `AppLifecycleState.resumed`/`.paused` in `didChangeAppLifecycleState`) —
 *    owned by `useChatPresence`.
 *  - `useInternetConnectivity` for the retry-on-reconnect trigger
 *    (Flutter `onInternetConnectivityChanged`, `chat_screen.dart:626-644`) —
 *    owned by `useChatRetryOnReconnect`.
 *
 * Architectural notes:
 *  - Encryption is owned by the `InitializeTextEncryptionUseCase` /
 *    `EncryptTextUseCase` / `DecryptTextUseCase` trio. The hook invokes
 *    `initializeTextEncryptionUseCase.execute(peer.publicJwk)` once per peer
 *    inside `runInitialLoad`; the chat use cases call encrypt/decrypt
 *    internally and no longer accept callback args. The hook does not import
 *    `textEncryptionService` directly.
 *  - Presence dispatches (both axes — route and tab) all flow through
 *    `useChatPresence.claim() / .release()` so the action name is centralized.
 *  - No unit test is required (the convention only mandates tests for use
 *    cases; feature hooks are exercised via integration / manual).
 *
 * Co-located helpers (kept small, extracted to fit under the file-size
 * threshold in `presentation.md`):
 *  - `useChatLiveListener` — the three `added` / `modified` / `removed`
 *    handlers (the desc-by-timestamp comparator lives on `ChatUtils`).
 *  - `useChatPresence` — the tab-visibility axis + `claim` / `release`.
 *  - `useChatRetryOnReconnect` — the `isOnline` transition watcher.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { Unsubscribe } from 'firebase/firestore';
import { useSelector } from 'react-redux';

import type { RootState } from '@/presentation/store';
import { selectHasActiveSubscription } from '@/presentation/store/slices/exclusiveContent/exclusiveContentSlice';

import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { MediaType } from '@/domain/enums/mediaType';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';

import { FetchChatMessagesUseCase } from '@/domain/usecases/chat/fetchChatMessagesUseCase';
import { SubscribeToChatUpdatesUseCase } from '@/domain/usecases/chat/subscribeToChatUpdatesUseCase';
import { SendTextChatMessageUseCase } from '@/domain/usecases/chat/sendTextChatMessageUseCase';
import { SendMediaChatMessageUseCase } from '@/domain/usecases/chat/sendMediaChatMessageUseCase';
import { MarkPeerMessageSeenUseCase } from '@/domain/usecases/chat/markPeerMessageSeenUseCase';
import { IsConversationBlockedUseCase } from '@/domain/usecases/chat/isConversationBlockedUseCase';
import { PeerIsSubscribedToExclusiveContentUseCase } from '@/domain/usecases/chat/peerIsSubscribedToExclusiveContentUseCase';
import { getUserUseCase } from '@/domain/usecases/user/getUserUseCase';

import { initializeTextEncryptionUseCase } from '@/domain/usecases/textEncryption/initializeTextEncryptionUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';

import { useInternetConnectivity } from '@/presentation/hooks/useInternetConnectivity';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import type { SendMessageMediaArgs } from '@/presentation/feature/publish/components/sheets/addContent/hooks/useSendMessageMedia';

import { useChatLiveListener } from './useChatLiveListener';
import { useChatPresence } from './useChatPresence';
import { useChatRetryOnReconnect } from './useChatRetryOnReconnect';

/**
 * Collected media payload handed to `useChat.sendMedia` by the AddContent
 * sheet's chat "Done" path (`useSendMessageMedia.submit`). The hook owns the
 * optimistic-tile insert + the persistence call; the sheet only collects the
 * file and its metadata. Mirrors the leading fields of
 * `SendMediaChatMessageArgs` (the use case the hook invokes) minus the
 * `chatUsersInfo` / `chatDocumentId` — both of which the hook supplies itself
 * (the info from its own `chatUsersInfoRef`, the id from the optimistic entry
 * it generates).
 *
 * Alias of the sheet's shared `SendMessageMediaArgs` — the chat and community
 * "Done" paths collect the identical `{ file, mediaType, aspectRatio,
 * durationSeconds }` shape and run the same submit hook.
 */
export type SendChatMediaArgs = SendMessageMediaArgs;

export interface UseChatArgs {
  /** URL `:peerId` parameter from the route. Stable for the life of the page. */
  peerIdParam: string;
  /**
   * Pre-resolved `ChatUsersInfoEntity` from `location.state` (in-app
   * navigation path — `UserDetailsPage -> Navigator.navigateToChat` forwards
   * the full struct). When present, the hook seeds its `chatUsersInfo` state
   * with this value and skips the deep-link rehydration round-trip. When
   * `null` (deep-link / refresh path where `location.state` is absent), the
   * hook resolves the peer via `getUserUseCase` during initialization and
   * builds the entity inline.
   */
  initialChatUsersInfo: ChatUsersInfoEntity | null;
}

export interface UseChatResult {
  /**
   * The resolved `ChatUsersInfoEntity`. `null` while deep-link rehydration is
   * in flight (the page should render a loading indicator gated on
   * `isFirstLoad` OR `chatUsersInfo === null`). Populated synchronously when
   * the caller supplied `initialChatUsersInfo`.
   */
  chatUsersInfo: ChatUsersInfoEntity | null;
  /** Newest-first message list (lower index = newer). Sorted desc by timestamp. */
  messages: ChatMessageEntity[];
  /** The resolved peer user; null until the initial fetch completes (or fails). */
  peerUser: UserEntity | null;
  /**
   * True until the first page has been fetched (mirrors Flutter
   * `_isFirstLoad`). On the deep-link path this also covers the rehydration
   * round-trip — stays `true` from mount until both the peer is resolved AND
   * the first message page lands.
   */
  isFirstLoad: boolean;
  /** True while a pagination page is in-flight (mirrors Flutter `_isFetching`). */
  isFetchingMore: boolean;
  /** True iff a further page exists (mirrors Flutter `_hasMore`). */
  hasMore: boolean;
  /** True iff either the current user is subscribed to the peer OR the inverse. */
  isSubscribedMutual: boolean;
  /**
   * Page-level conversation-blocked flag: true iff the current user has the
   * conversation locally blocked OR the peer has blocked the conversation with
   * the current user. Drives the composer "Conversation blocked" replacement +
   * the app-bar video-call-icon drop. Mirrors Flutter's
   * `_currentUserConversationBlocked || _peerUserConversationBlocked` gate at
   * `chat_screen.dart:1197` (app bar) / `:3541` (composer). The local term
   * flips live via `setConversationBlocked` (Task 2); the peer term is derived
   * from the already-fetched `peerUser` and so reflects on the page's next open
   * (no live listener — `chat_screen.dart:378-381`).
   */
  isConversationBlocked: boolean;
  /**
   * Imperatively set the conversation-blocked flag. Invoked from the
   * chat-actions sheet's successful block/unblock path
   * (`ChatActionsSheetContent` → `ChatActionsSheet.show.onConversationBlockChanged`)
   * so the composer "Conversation blocked" replacement + the app-bar
   * video-call-icon drop flip *live*, without popping + re-navigating the page.
   * Mirrors Flutter's post-action re-read: the chat screen keeps
   * conversation-blocked as screen-local `setState` booleans
   * (`chat_screen.dart:86-87`) and re-reads them on `didPopNext`; the web lifts
   * the new state through this setter at the moment of the successful mutation.
   * Stable reference (`useCallback` with `[]` deps).
   *
   * Mutates ONLY the current-user-local term of the page-level
   * `isConversationBlocked` flag. The peer-side term (the peer having blocked
   * the conversation with the current user) is derived from `peerUser` and is
   * NOT settable by this client.
   */
  setConversationBlocked: (block: boolean) => void;
  /** True iff `currentUser.blocked || currentUser.suspended` (`chat_screen.dart:215-216`). */
  currentUserBlocked: boolean;
  /** True iff `currentUser.usersBlockedMe?.includes(peerId)` (`chat_screen.dart:216-218`). */
  currentUserBlockedByPeer: boolean;
  /** True iff `peerUser.blocked || peerUser.suspended` (`chat_screen.dart:374-381`). */
  peerUserBlocked: boolean;
  /** Live `navigator.onLine` flag mirrored from `useInternetConnectivity`. */
  isOnline: boolean;
  /** Transient banner flag — true for ~3s after a failed text send. */
  sendError: boolean;
  /**
   * Map keyed by `message.timestamp` — true means the message's timestamp
   * adornment is currently open. Tapping a bubble flips the entry for that
   * message and forces every other entry to `false` ("only one open at a
   * time"; `chat_screen.dart:1405-1413`).
   */
  openTimestampForMessageId: Record<number, boolean>;
  /** Number of unread peer messages received while scrolled away from bottom. */
  unreadCount: number;
  /**
   * True for ~3 s after every peer-message-while-scrolled-away arrival.
   * Mirrors Flutter's `_unreadMessageReceived` flag
   * (`chat_screen.dart:566-578`): the pill renders the most-recent unread
   * for 3 s then degrades back to `ScrolledUpButton`; each new peer arrival
   * restarts the cycle. Page gates `UnreadBanner`'s pill branch on
   * `unreadBannerVisible && unreadPreview != null`.
   */
  unreadBannerVisible: boolean;
  /**
   * True iff the user is currently scrolled away from the bottom of the
   * (reversed) list. Mirrors Flutter's `_isScrolledUp`
   * (`chat_screen.dart:3399-3400`) — exposed alongside `unreadCount` so the
   * page can build the `_isScrolledUp || _unreadMessageReceived` OR predicate
   * that gates the `UnreadBanner` (its `ScrolledUpButton`-only branch is only
   * reachable when this is true and no unread items are bucketed).
   */
  isScrolledUp: boolean;
  /**
   * The list of unread peer messages currently bucketed (newest-first, sorted
   * the same way the main `messages` list is). Exposed so the page can format
   * the most-recent unread item into the `UnreadBanner` preview pill (see
   * `useExpauseLocalizations().expauseLocalizations.getUnreadPreview`).
   * Flushed to `messages` on `onScrolledToBottom`.
   */
  unreadItems: ChatMessageEntity[];
  /** Index in the desc-sorted `messages` array of the most recent own message marked seenByPeer, or -1 if none. */
  myLastMessageReadPosition: number;
  /**
   * Monotonically-increasing counter incremented every time `sendText`
   * inserts a new optimistic message. The list owner watches this token
   * (`ChatMessagesRegion`'s `useEffect`) and drives the
   * `ChatMessagesList.scrollToBottom()` handle on change so the just-sent
   * bubble lands visible — mirrors Flutter `chat_screen.dart:670-684`
   * (`_listScrollController.animateTo(0.0, …)` after every send). Tied to
   * `sendText` only (not to the live-listener `added` branch); incoming
   * messages rely on `VirtualList`'s built-in at-bottom anchor and the
   * unread-banner CTA when scrolled away.
   */
  scrollToBottomToken: number;
  /**
   * Send a plaintext text message. Builds the entity client-side, inserts
   * optimistically at index 0, and persists via `SendTextChatMessageUseCase`.
   * On failure the optimistic entry is flipped to `success=false` and the
   * send-error banner is raised for 3s.
   */
  sendText: (text: string) => Promise<void>;
  /**
   * Send a media (image / video) message. Mirrors `sendText`'s optimistic
   * pattern — a deliberate divergence from Flutter, which only writes the
   * Firestore doc AFTER the upload finishes (so nothing renders during a long
   * upload). The hook builds an optimistic `ChatMessageEntity` with
   * `isUploading: true` (so the bubble renders the "Processing" placeholder
   * immediately), inserts it at index 0, bumps `scrollToBottomToken`, then runs
   * the upload + persist via `SendMediaChatMessageUseCase` — passing the
   * optimistic entry's `documentId` through as `chatDocumentId` so the persisted
   * doc shares one id with the optimistic tile and the live-listener echo
   * REPLACES it (no duplicate). On failure the optimistic entry is flipped to
   * `success: false, isUploading: false` in place — the existing read-receipt
   * `error.png` affordance renders automatically (no new failure UI), and the
   * tile is removed/retried via `retryMessage` rather than vanishing silently.
   * Called by the AddContent sheet's chat "Done" path (`useSendMessageMedia`),
   * which collects the file + metadata and closes the sheet immediately.
   * Resolves the upload-success boolean so that path can toast on failure.
   */
  sendMedia: (args: SendChatMediaArgs) => Promise<boolean>;
  /**
   * Re-run a single failed message. The bubble dispatches this with the exact
   * message that failed (`MessageBubble.onRetry`); used by the per-bubble retry
   * tap. For text it re-runs `SendTextChatMessageUseCase`; for media it
   * re-uploads the cached `File` (same `documentId`), or removes the tile if
   * the `File` is no longer cached (in-memory only). No-op for other types.
   */
  retryMessage: (message: ChatMessageEntity) => Promise<void>;
  /**
   * Re-run every failed text message through `SendTextChatMessageUseCase`
   * (reverse order, matches Flutter `chat_screen.dart:626-644`). Used by the
   * reconnect handler (`useChatRetryOnReconnect`), which has no per-message
   * context.
   */
  retryAllFailed: () => Promise<void>;
  /**
   * The list owner calls this when the user has scrolled to the bottom of
   * the (reversed) list, i.e. is viewing the newest message. Mirrors the
   * Flutter `_currentScroll <= 0` branch — flushes unread items into the
   * main list and marks the latest peer message seen.
   */
  onScrolledToBottom: () => void;
  /**
   * The list owner calls this on the inverse transition — when the user
   * scrolls away from the bottom. Required so the live-listener `added`
   * branch can route subsequent peer messages into the unread bucket from
   * the very first one (Flutter tracks `_currentScroll` continuously on the
   * scroll listener; the web boolean needs an explicit signal in both
   * directions).
   */
  onScrolledAway: () => void;
  /** The list owner calls this when the top sentinel intersects ("load older"). */
  onLoadMore: () => void;
  /** Tap-to-toggle timestamp adornment for the given message (`chat_screen.dart:1405-1413`). */
  toggleTimestamp: (message: ChatMessageEntity) => void;
  // --- Page lifecycle handlers (Finding 31) ---
  //
  // The host page wires these into its own `useBasePage` call. The hook owns
  // every state mutation each phase triggers; the page only forwards the
  // event. Each is a stable `useCallback` reference so `useBasePage`'s
  // internal effect does not refire on every render.
  /**
   * `useBasePage.onInit` target. Idempotent — guarded by an internal "has
   * already initialized" ref. Performs deep-link rehydration (when needed)
   * and the full Flutter-parity `_initialFetch` flow (peer resolution,
   * presence claim, E2EE init, page-1 fetch, mark-seen, live listener
   * bootstrap).
   */
  onPageInit: () => void;
  /**
   * `useBasePage.onPopNext` target. Re-claim presence when the page becomes
   * visible again after a pushed-on-top route was popped.
   */
  onPageVisible: () => void;
  /**
   * `useBasePage.onPushNext` / `onPop` target. Release presence when the
   * page becomes hidden (either a new route pushed on top, or this route is
   * being popped). Same dispatch in both cases — mirrors Flutter `didPushNext`
   * and `didPop` both clearing `_userChattingWith`.
   */
  onPageHidden: () => void;
  /**
   * `useBasePage.onDispose` target. Final teardown — release presence, tear
   * down the live listener, clear any pending send-error timer.
   */
  onPageDispose: () => void;
}

export const useChat = ({
  peerIdParam,
  initialChatUsersInfo,
}: UseChatArgs): UseChatResult => {
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // `chatUsersInfo` is held as state so deep-link rehydration can fill it in
  // during `onPageInit`. Seeded synchronously from `initialChatUsersInfo`
  // when the in-app nav path supplied it (no rehydration round-trip needed).
  // The init function form makes sure the seed only applies on the first
  // render — the active peer never changes for the life of a mounted page.
  const [chatUsersInfo, setChatUsersInfo] = useState<ChatUsersInfoEntity | null>(
    () => initialChatUsersInfo,
  );

  // Selector: current user is subscribed to peer's exclusive content.
  // Inverse direction (peer subscribed to current user) requires a Firestore
  // round-trip via `PeerIsSubscribedToExclusiveContentUseCase` — held in
  // local state below. Use `peerIdParam` (always present, stable) rather
  // than `chatUsersInfo?.peerId` so the selector binds on first render.
  const currentUserSubscribedToPeer = useSelector((state: RootState) =>
    selectHasActiveSubscription(state, peerIdParam),
  );

  const { isOnline } = useInternetConnectivity();

  // --- Use-case instances ---
  // Instantiated once per hook lifetime. Constructors default-bind to the
  // shared `ChatService` singleton (and the `ExclusiveContentSubscribersService`
  // singleton for the subscriber check), so this is a no-op-cheap line.
  const fetchChatMessagesUseCase = useMemo(() => new FetchChatMessagesUseCase(), []);
  const subscribeToChatUpdatesUseCase = useMemo(() => new SubscribeToChatUpdatesUseCase(), []);
  const sendTextChatMessageUseCase = useMemo(() => new SendTextChatMessageUseCase(), []);
  const sendMediaChatMessageUseCase = useMemo(() => new SendMediaChatMessageUseCase(), []);
  const markPeerMessageSeenUseCase = useMemo(() => new MarkPeerMessageSeenUseCase(), []);
  const isConversationBlockedUseCase = useMemo(() => new IsConversationBlockedUseCase(), []);
  const peerIsSubscribedToExclusiveContentUseCase = useMemo(
    () => new PeerIsSubscribedToExclusiveContentUseCase(),
    [],
  );

  // --- Local state ---
  const [messages, setMessages] = useState<ChatMessageEntity[]>([]);
  // `null` until `runInitialLoad` fetches the peer (the in-app navigation
  // path supplies `initialChatUsersInfo` but never a pre-fetched
  // `UserEntity` — the deep-link path resolves it inside the hook now that
  // rehydration moved here).
  const [peerUser, setPeerUser] = useState<UserEntity | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [peerSubscribedToCurrentUser, setPeerSubscribedToCurrentUser] = useState<boolean>(false);
  const [isConversationBlocked, setIsConversationBlocked] = useState<boolean>(false);
  const [sendError, setSendError] = useState<boolean>(false);
  const [openTimestampForMessageId, setOpenTimestampForMessageId] = useState<
    Record<number, boolean>
  >({});
  // `_unreadListItems` in Flutter — the peer messages arrived while the user
  // is scrolled away from the bottom. Held as state so the banner re-renders
  // with the latest count; flushed into `messages` on `onScrolledToBottom`.
  const [unreadItems, setUnreadItems] = useState<ChatMessageEntity[]>([]);

  // Transient flag mirroring Flutter's `_unreadMessageReceived`
  // (`chat_screen.dart:566-578`). Flipped to `true` on every peer-message-
  // while-scrolled-away arrival; an auto-hide timer in `useChatLiveListener`
  // flips it back to `false` after 3 s. The page renders the pill iff this
  // is `true` AND there is a preview to show; otherwise the surface
  // degrades back to `ScrolledUpButton`. Reset to `false` (and timer
  // cleared) when the user scrolls back to the bottom and when the page
  // disposes.
  const [unreadBannerVisible, setUnreadBannerVisible] = useState<boolean>(false);
  // Pending auto-hide timer handle for `unreadBannerVisible`. Held in a ref
  // so the listener (which runs outside React's render closure) can clear /
  // reschedule it across consecutive peer arrivals without taking a stale
  // snapshot.
  const unreadBannerTimerRef = useRef<number | null>(null);

  // Monotonic counter — incremented on every successful optimistic insert
  // inside `sendText`. The list owner watches this in a `useEffect` and
  // drives `ChatMessagesList.scrollToBottom()` so the just-sent bubble lands
  // visible (Flutter `chat_screen.dart:670-684`). Held as state (not a ref)
  // so the consumer's `useEffect` actually re-runs on the change.
  const [scrollToBottomToken, setScrollToBottomToken] = useState<number>(0);

  // --- Refs used inside async callbacks ---
  // We need a fresh read of certain values from inside the live-listener
  // handlers (which are registered ONCE) and the retry loop; React-state
  // closures would otherwise snapshot stale values.
  const messagesRef = useRef<ChatMessageEntity[]>([]);
  messagesRef.current = messages;

  const unreadItemsRef = useRef<ChatMessageEntity[]>([]);
  unreadItemsRef.current = unreadItems;

  // Pagination cursor — the timestamp of the oldest message in the most
  // recent page (Firestore `startAfter` cursor). Mirrors Flutter
  // `_lastFetchedDocument` semantically (we use the timestamp because the
  // web data layer's pagination contract is `(groupChatId, lastTimestamp,
  // limit) → page`; see `chatService.fetchMessagesPage`).
  const lastTimestampRef = useRef<number | null>(null);

  // First-load completion cursor for the live listener — the timestamp of
  // the newest message at the moment the listener starts. Mirrors Flutter
  // `_mostRecentChatMessage.timestamp` (`chat_screen.dart:484-487`).
  // Defaults to "now" when the chat is empty, exactly per Flutter.
  // Held as a ref so the listener setup doesn't re-trigger.
  const listenerCursorRef = useRef<number | null>(null);

  // Live-listener cleanup handle. Held in a ref so the cleanup phase of the
  // mount effect can read it without re-running on every render.
  const liveUnsubscribeRef = useRef<Unsubscribe | null>(null);

  // True while a pagination request is in-flight. Held as a ref (NOT only
  // state) so `onLoadMore` can short-circuit synchronously when called
  // multiple times in quick succession (the VirtualList sentinel can fire
  // repeatedly during a slow load).
  const isFetchingRef = useRef<boolean>(false);

  // True iff the user is currently scrolled near the bottom of the list.
  // Used by the live-listener `added` branch: a peer message arriving while
  // the user is at the bottom is inserted into the visible list AND marked
  // seen; otherwise it lands in the unread bucket. Driven explicitly by
  // `onScrolledToBottom` / `onScrolledAway` from the list owner so both
  // directions of the bottom-pin boolean reach the handler (Flutter's
  // continuous-scroll behavior — `chat_screen.dart:547-579`).
  const isAtBottomRef = useRef<boolean>(true);

  // Web mirror of Flutter `_appLifecycleState` (`chat_screen.dart:131-153`):
  // `true` when the browser tab is visible (Flutter `resumed` / `index == 0`),
  // `false` when hidden (Flutter `paused` / `index == 2`). The live-listener
  // `added` branch reads this synchronously to gate the peer path — a hidden
  // tab routes incoming peer messages to the unread bucket instead of
  // insert + mark-seen, REGARDLESS of `isAtBottomRef`, so a backgrounded
  // reader never sends a false read-receipt (Flutter's background-first branch
  // at `chat_screen.dart:543-546`). Defaults to `true` under SSR / when
  // `document` is undefined so server rendering and tests are unaffected. Kept
  // fresh by the SINGLE `useChatPresence` tab-visibility subscription via its
  // `onVisibilityChange` arg (no second `visibilitychange` listener).
  const isTabVisibleRef = useRef<boolean>(
    typeof document === 'undefined'
      ? true
      : document.visibilityState === 'visible',
  );

  // React-state mirror of `isAtBottomRef` (inverted). Held as state so the
  // page can re-render the `UnreadBanner` when the user scrolls away from
  // (or back to) the bottom even when no unread items are bucketed — that
  // is the only path that reaches the `ScrolledUpButton` branch. Mirrors
  // Flutter's `_isScrolledUp` (`chat_screen.dart:3399-3400`). The ref above
  // is still the source of truth for synchronous reads inside the
  // live-listener `added` handler; this state shadows it for render needs.
  const [isScrolledUp, setIsScrolledUp] = useState<boolean>(false);

  // Pending hide-timer for the send-error banner. Reset on every new
  // failure so consecutive failures restart the 3s window.
  const sendErrorTimerRef = useRef<number | null>(null);

  // In-flight media `File`s keyed by the optimistic message `documentId`.
  // The original `File` is NOT stored on `ChatMessageEntity` and is gone once
  // the AddContent sheet closes, so we cache it here at optimistic-insert time
  // to enable a true re-upload retry on a failed media tile (`retryMessage`'s
  // media branch). Cleared on success (the upload resolved) and on remove —
  // so a successful tile never retains its (potentially large) blob.
  const sendMediaFilesRef = useRef<Map<string, SendChatMediaArgs>>(new Map());

  // Guards to ensure single-fire side-effects.
  const hasStartedListenerRef = useRef<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);

  // Always-current snapshot of `chatUsersInfo`. The state value can be `null`
  // on the deep-link path until rehydration completes; lifecycle callbacks
  // and live-listener side-effects read through this ref so they can act on
  // the post-rehydrate value without waiting for a re-render closure.
  const chatUsersInfoRef = useRef<ChatUsersInfoEntity | null>(chatUsersInfo);
  chatUsersInfoRef.current = chatUsersInfo;

  // Always-current snapshot of `peerUser`. `runMediaSendForId` (and the retry
  // path that re-invokes it) is a memoized callback that closes over the
  // initial render's `peerUser` (null on first mount). It reads the peer's
  // `publicJwk` through this ref so the encrypted-media upload hop
  // (`SendMediaChatMessageUseCase` → `chatService.uploadChatMedia`) can
  // defensively (re-)derive the RIGHT peer key before `EncryptBinaryUseCase`
  // runs, without re-creating the callback (and without threading the JWK
  // through the sheet submit site, which never resolves the peer). The JWK is
  // also derived once at chat load (`runInitialLoad` step 3, `:756`); this
  // forward keeps the per-send guard correct even if that singleton drifts.
  const peerUserRef = useRef<UserEntity | null>(peerUser);
  peerUserRef.current = peerUser;

  // --- Derived blocked flags ---
  // Per `chat_screen.dart:215-218, 374-381`. We compute these from `currentUser`
  // and `peerUser` rather than caching them in state, so they stay live with
  // the auth-slice / peer-user updates. Use `peerIdParam` (URL-stable) for
  // the "blocked me by peer" lookup — equals `chatUsersInfo.peerId` once
  // rehydrated, and gives the correct value on first render too.
  const currentUserBlocked: boolean = currentUser
    ? currentUser.blocked || currentUser.suspended
    : false;
  const currentUserBlockedByPeer: boolean =
    currentUser?.usersBlockedMe?.includes(peerIdParam) ?? false;
  const peerUserBlocked: boolean = peerUser
    ? peerUser.blocked || peerUser.suspended
    : false;

  // Peer-side conversation-block term (`chat_screen.dart:378-381`): the peer
  // has the current user in *their* `blockedConversations` list. Derived from
  // the already-fetched `peerUser` (no extra Firestore read / listener) so it
  // reflects the value as of the page's last open. ORed with the
  // current-user-local term below to form the page-level
  // conversation-blocked flag.
  const peerUserConversationBlocked: boolean =
    ChatUtils.peerUserConversationBlocked(
      peerUser,
      currentUser?.firebaseId ?? '',
    );

  // Page-level conversation-blocked flag. Mirrors Flutter's
  // `_currentUserConversationBlocked || _peerUserConversationBlocked` gate at
  // `chat_screen.dart:1197` (app bar) / `:3541` (composer). The local term
  // (`isConversationBlocked` state) flips live via `setConversationBlocked`
  // (Task 2); the peer term is derived from `peerUser` and is not settable by
  // this client.
  const conversationBlocked: boolean =
    isConversationBlocked || peerUserConversationBlocked;

  const isSubscribedMutual: boolean =
    currentUserSubscribedToPeer || peerSubscribedToCurrentUser;

  // Memo: index of the most recent own message marked seenByPeer.
  // Mirrors Flutter `myLastMessageReadPosition` (`chat_screen.dart:760-771`):
  // iterates desc-sorted list, returns the FIRST own message with
  // `seenByPeer === true`, or -1 if none.
  const myLastMessageReadPosition = useMemo<number>(() => {
    if (!currentUser) return -1;
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      if (m.idFrom === currentUser.firebaseId && m.seenByPeer) {
        return i;
      }
    }
    return -1;
  }, [messages, currentUser]);

  // Re-arm the transient unread pill for 3 s, mirroring the timer logic in
  // `useChatLiveListener.handleAdded` so there is exactly ONE auto-hide timer
  // (`unreadBannerTimerRef`). Used by the tab-resume branch below to replicate
  // the resume half of Flutter's lifecycle handler (`chat_screen.dart:141-152`):
  // when the app returns to the foreground and a peer message arrived while
  // backgrounded (`_unreadMessageReceived`), Flutter re-shows the pill for 3 s.
  // On the web the hidden-tab timer scheduled at arrival has almost always
  // already expired unseen (background tabs still fire `setTimeout`), so the
  // pill must be re-raised on resume for the preview to actually be seen.
  const raiseUnreadPill = useCallback(() => {
    setUnreadBannerVisible(true);
    if (unreadBannerTimerRef.current !== null) {
      window.clearTimeout(unreadBannerTimerRef.current);
    }
    unreadBannerTimerRef.current = window.setTimeout(() => {
      setUnreadBannerVisible(false);
      unreadBannerTimerRef.current = null;
    }, ANIMATION_CONSTANTS.duration.autoHideDelay);
  }, []);

  // --- Presence helpers (route + tab axes) ---
  // `useChatPresence` owns the tab-visibility axis directly; the route axis
  // is driven by the page's `useBasePage`, which forwards `onPopNext` /
  // `onPushNext` / `onPop` into our `onPageVisible` / `onPageHidden`
  // callbacks below. We pass `peerIdParam` (URL-stable, always present) so
  // the dispatch resolves on first render — equivalent to
  // `chatUsersInfo.peerId` once rehydrated, but available before.
  const { claim: claimPresence, release: releasePresence } = useChatPresence(
    peerIdParam,
    // Keep `isTabVisibleRef` (the web mirror of Flutter `_appLifecycleState`)
    // fresh from the SINGLE chat-presence visibility subscription — no second
    // `visibilitychange` listener. The live-listener `added` background gate
    // reads this ref synchronously (`chat_screen.dart:543-546`).
    (isVisible) => {
      isTabVisibleRef.current = isVisible;
      // Flutter resume branch (`chat_screen.dart:141-152`): on return to the
      // foreground, if any peer message arrived while hidden, re-show the
      // unread pill for 3 s. The hidden-tab timer scheduled at arrival has
      // usually already expired unseen, so re-arm it here.
      if (isVisible && unreadItemsRef.current.length > 0) {
        raiseUnreadPill();
      }
    },
  );

  // --- Live-listener handlers (Flutter `chat_screen.dart:497-621`) ---
  //
  // The handlers are bound exactly ONCE to the Firestore subscription, so
  // they read `chatUsersInfo` (for `currentFirebaseId` / `groupChatId`) via
  // a *stable* derived skeleton. The skeleton is constructed synchronously
  // from `peerIdParam` + `currentUser` so it is non-null on first render;
  // peerName / currentName / createdTimestamp fall back to placeholders
  // because the listener never reads them.
  const listenerChatUsersInfo = useMemo<ChatUsersInfoEntity>(() => {
    const cu = currentUser;
    return {
      currentFirebaseId: cu?.firebaseId ?? '',
      peerId: peerIdParam,
      currentName: cu?.name ?? '',
      peerName: chatUsersInfo?.peerName ?? '',
      createdTimestamp: chatUsersInfo?.createdTimestamp ?? 0,
      groupChatId: cu?.firebaseId
        ? ChatUtils.getGroupChatId(cu.firebaseId, peerIdParam)
        : undefined,
    };
  }, [
    currentUser,
    peerIdParam,
    chatUsersInfo?.peerName,
    chatUsersInfo?.createdTimestamp,
  ]);

  const { handleAdded, handleModified, handleRemoved } = useChatLiveListener({
    chatUsersInfo: listenerChatUsersInfo,
    isAtBottomRef,
    isTabVisibleRef,
    markPeerMessageSeenUseCase,
    setMessages,
    setOpenTimestampForMessageId,
    setUnreadItems,
    setUnreadBannerVisible,
    unreadBannerTimerRef,
  });

  // --- Initial load + listener bootstrap ---

  /**
   * Initial data load. Mirrors `chat_screen.dart:210-225` + `:398-479`,
   * plus deep-link rehydration (the URL-only entry path the page used to
   * own — moved here in Finding 31):
   *  0. (Deep-link path only) Resolve the peer via `GetUserUseCase` and
   *     build `ChatUsersInfoEntity` inline. The in-app navigation path
   *     supplied this entity via `initialChatUsersInfo`; that branch
   *     reuses the already-fetched peer in step 1 below.
   *  1. Resolve peer user via `GetUserUseCase` (skipped on the deep-link
   *     path — the rehydrate step already fetched it).
   *  2. Claim presence — Flutter `_setUserChattingWithPeer` is explicitly
   *     gated on `_peerUser != null` (`chat_screen.dart:313-321`); we
   *     mirror that ordering here.
   *  3. Initialize E2EE key from `peerUser.publicJwk`.
   *  4. Probe the local "conversation blocked" record + the
   *     `peerIsSubscribedToCurrentUser` Firestore query (parallel with the
   *     page fetch).
   *  5. Fetch page 1.
   *  6. If the most-recent message is a peer message, mark it seen
   *     (`chat_screen.dart:459-470`).
   *  7. Start the live listener using the most-recent timestamp as the
   *     `where("timestamp", isGreaterThan: …)` cursor.
   */
  const runInitialLoad = useCallback(async () => {
    if (!currentUser) return;

    try {
      // --- Step 0: deep-link rehydration (if needed) ---
      //
      // When `initialChatUsersInfo` was null (URL-only entry, e.g. user
      // refreshed the tab or opened a `/chat/:peerId` link directly), we
      // need to fetch the peer first to build the entity. We hold the
      // pre-fetched peer in a local so step 1 can reuse it (single
      // Firestore read for the doc).
      let resolvedInfo: ChatUsersInfoEntity | null = chatUsersInfoRef.current;
      let prefetchedPeer: UserEntity | null = null;

      if (resolvedInfo === null) {
        const peer = await getUserUseCase.execute(peerIdParam);
        if (peer === null) {
          appLogger.error(
            'useChat: peer user not found on deep-link rehydrate',
            'useChat.runInitialLoad',
            { error: new Error(`peerId=${peerIdParam} not found`) },
          );
          return;
        }
        prefetchedPeer = peer;
        resolvedInfo = {
          currentFirebaseId: currentUser.firebaseId,
          peerId: peer.firebaseId,
          currentName: currentUser.name,
          peerName: peer.name,
          createdTimestamp: 0,
          groupChatId: ChatUtils.getGroupChatId(
            currentUser.firebaseId,
            peer.firebaseId,
          ),
        };
        setChatUsersInfo(resolvedInfo);
      }

      // Local non-null binding so subsequent steps don't have to re-narrow.
      const info = resolvedInfo;

      if (!info.groupChatId) {
        // No groupChatId means the chat doc hasn't been created yet (a
        // brand-new conversation with no messages). The first send will
        // create it. Mark first-load complete so the empty-state UI can
        // render.
        setIsFirstLoad(false);
        return;
      }

      // 1 + 4 (parallel): peer user fetch + conversation-blocked probe +
      // peer-subscribed-to-current-user probe.
      //
      // The deep-link path already fetched the peer in step 0; reuse it
      // so we don't hit Firestore twice for the same doc.
      const peerFetchPromise: Promise<UserEntity | null> =
        prefetchedPeer !== null
          ? Promise.resolve(prefetchedPeer)
          : getUserUseCase.execute(info.peerId);

      const [resolvedPeer, localConversationBlocked, peerSubscribed] =
        await Promise.all([
          peerFetchPromise,
          isConversationBlockedUseCase.execute(info.peerId),
          peerIsSubscribedToExclusiveContentUseCase.execute(info.peerId),
        ]);

      setPeerUser(resolvedPeer);
      setIsConversationBlocked(localConversationBlocked);
      setPeerSubscribedToCurrentUser(peerSubscribed);

      // 2: claim presence — only after peer resolution succeeds (Flutter
      // `if (_peerUser != null) { _setUserChattingWithPeer(); }`,
      // `chat_screen.dart:313-321`). Tab-visibility and route-visibility
      // axes keep their own dispatches (peer is already known there).
      if (resolvedPeer !== null) {
        claimPresence();
      }

      // 3: initialize E2EE for this peer.
      await initializeTextEncryptionUseCase.execute(resolvedPeer?.publicJwk ?? null);

      // 5: fetch page 1.
      const limit = SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION;
      const page = await fetchChatMessagesUseCase.execute(
        info.groupChatId,
        null,
        limit,
      );

      // Seed the timestamp-open map: every loaded message starts closed.
      const initialTimestampMap: Record<number, boolean> = {};
      for (const m of page.messages) {
        initialTimestampMap[m.timestamp] = false;
      }
      setOpenTimestampForMessageId((prev) => ({ ...initialTimestampMap, ...prev }));

      setMessages(page.messages);
      lastTimestampRef.current = page.lastTimestamp;
      setHasMore(page.hasMore);

      // 6: mark the most-recent as seen if it's a peer message
      // (`chat_screen.dart:459-470`).
      const mostRecent = page.messages[0];
      if (
        mostRecent !== undefined &&
        mostRecent.idFrom !== info.currentFirebaseId
      ) {
        void markPeerMessageSeenUseCase
          .execute(info.groupChatId, mostRecent)
          .catch((error: unknown) => {
            appLogger.error(
              'markPeerMessageSeen (initial-load) failed',
              'useChat.runInitialLoad',
              { error: error as Error },
            );
          });
      }

      // 7: start the live listener. Cursor = newest message's timestamp,
      // or `Date.now()` for an empty chat (`chat_screen.dart:484-487`).
      listenerCursorRef.current = mostRecent?.timestamp ?? Date.now();
      if (!hasStartedListenerRef.current) {
        hasStartedListenerRef.current = true;
        liveUnsubscribeRef.current = subscribeToChatUpdatesUseCase.execute(
          info.groupChatId,
          listenerCursorRef.current,
          {
            onAdded: handleAdded,
            onModified: handleModified,
            onRemoved: handleRemoved,
          },
        );
      }
    } catch (error) {
      appLogger.error('Initial chat load failed', 'useChat.runInitialLoad', {
        error: error as Error,
      });
    } finally {
      setIsFirstLoad(false);
    }
  }, [
    currentUser,
    peerIdParam,
    fetchChatMessagesUseCase,
    isConversationBlockedUseCase,
    peerIsSubscribedToExclusiveContentUseCase,
    subscribeToChatUpdatesUseCase,
    markPeerMessageSeenUseCase,
    claimPresence,
    handleAdded,
    handleModified,
    handleRemoved,
  ]);

  // --- onLoadMore (pagination) ---

  const onLoadMore = useCallback((): void => {
    if (isFetchingRef.current || !hasMore) {
      return;
    }

    const info = chatUsersInfoRef.current;
    if (!info?.groupChatId) return;
    if (lastTimestampRef.current === null) return; // first-load not complete yet
    isFetchingRef.current = true;
    setIsFetchingMore(true);

    void (async () => {
      try {
        const limit = SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION;
        const page = await fetchChatMessagesUseCase.execute(
          info.groupChatId as string,
          lastTimestampRef.current,
          limit,
        );

        if (page.messages.length === 0) {
          setHasMore(page.hasMore);
          return;
        }

        // Merge: existing messages are newer (lower index), the new page is
        // older (higher index). Concat + sort handles any out-of-order
        // arrivals defensively. The sort is the same desc-by-timestamp rule
        // used everywhere else.
        setMessages((prev) => {
          const next = prev.concat(page.messages);
          next.sort(ChatUtils.sortByTimestampDesc);
          return next;
        });

        setOpenTimestampForMessageId((prev) => {
          const next = { ...prev };
          for (const m of page.messages) {
            if (next[m.timestamp] === undefined) {
              next[m.timestamp] = false;
            }
          }
          return next;
        });

        lastTimestampRef.current = page.lastTimestamp;
        setHasMore(page.hasMore);
      } catch (error) {
        appLogger.error('Paginate older messages failed', 'useChat.onLoadMore', {
          error: error as Error,
        });
      } finally {
        isFetchingRef.current = false;
        setIsFetchingMore(false);
      }
    })();
  }, [hasMore, fetchChatMessagesUseCase]);

  // --- sendText (optimistic-insert + persistence + send-error UX) ---

  /**
   * Helper: clear any pending send-error timer and start a fresh 3s one
   * (Flutter `Future.delayed(const Duration(seconds: 3), …)` at
   * `chat_screen.dart:707-712`).
   */
  const raiseSendErrorBanner = useCallback(() => {
    if (sendErrorTimerRef.current !== null) {
      window.clearTimeout(sendErrorTimerRef.current);
    }
    setSendError(true);
    sendErrorTimerRef.current = window.setTimeout(() => {
      setSendError(false);
      sendErrorTimerRef.current = null;
    }, ANIMATION_CONSTANTS.duration.autoHideDelay);
  }, []);

  /**
   * Send a single text message. Mirrors `chat_screen.dart:652-717` for the
   * text-only branch (round-one scope; other types are out of scope).
   */
  const sendText = useCallback(
    async (text: string): Promise<void> => {
      const plain = text.trim();
      if (plain === '') return;
      if (!currentUser) return;
      const info = chatUsersInfoRef.current;
      if (!info?.groupChatId) return;

      const optimistic: ChatMessageEntity = {
        documentId: generateUuidV1(),
        chatId: info.groupChatId,
        idFrom: currentUser.firebaseId,
        idTo: info.peerId,
        timestamp: Date.now(),
        type: ChatMessageType.text,
        seenByPeer: false,
        users: [currentUser.firebaseId, info.peerId],
        content: plain,
        success: true,
        giftInstances: 1,
      };

      // Optimistic insert at index 0 — lower index = newer in this
      // desc-sorted list (`chat_screen.dart:668-686`).
      setMessages((prev) => {
        const next = [optimistic, ...prev];
        next.sort(ChatUtils.sortByTimestampDesc);
        return next;
      });
      setOpenTimestampForMessageId((prev) => ({
        ...prev,
        [optimistic.timestamp]: false,
      }));
      // Signal the list owner to snap the viewport to the bottom on the
      // next render — Flutter `_listScrollController.animateTo(0.0, …)` at
      // `chat_screen.dart:670-684`. Bumped on every send (both at-bottom
      // and scrolled-away branches in Flutter call `animateTo(0.0)`).
      setScrollToBottomToken((prev) => prev + 1);

      try {
        const success = await sendTextChatMessageUseCase.execute({
          chatMessage: optimistic,
          chatUsersInfo: info,
        });

        if (!success) {
          // Flip the optimistic entry to `success=false` in place — mirrors
          // Flutter `chatMessage.success = false` (`chat_screen.dart:702`).
          setMessages((prev) =>
            prev.map((m) =>
              m.documentId === optimistic.documentId ? { ...m, success: false } : m,
            ),
          );
          raiseSendErrorBanner();
        }
      } catch (error) {
        appLogger.error('sendText failed', 'useChat.sendText', {
          error: error as Error,
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.documentId === optimistic.documentId ? { ...m, success: false } : m,
          ),
        );
        raiseSendErrorBanner();
      }
    },
    [
      currentUser,
      sendTextChatMessageUseCase,
      raiseSendErrorBanner,
    ],
  );

  // --- sendMedia (optimistic-insert + upload/persist + failure flip) ---

  /**
   * Shared upload+persist hop for a media message whose optimistic tile is
   * already in the list. Runs `SendMediaChatMessageUseCase.execute` with the
   * SAME `documentId` as the optimistic entry (so the live-listener echo
   * replaces, not duplicates), then either clears the cached `File` on success
   * or flips the tile to the failed-static state on failure. Reused by both
   * `sendMedia` (first attempt) and `retryMessage`'s media branch (re-upload).
   */
  const runMediaSendForId = useCallback(
    async (
      documentId: string,
      mediaArgs: SendChatMediaArgs,
      info: ChatUsersInfoEntity,
    ): Promise<boolean> => {
      try {
        const uploaded = await sendMediaChatMessageUseCase.execute({
          file: mediaArgs.file,
          mediaType: mediaArgs.mediaType,
          aspectRatio: mediaArgs.aspectRatio,
          durationSeconds: mediaArgs.durationSeconds,
          chatUsersInfo: info,
          // Share the optimistic tile's id with the persisted doc so the
          // live-listener `added` echo (documentId-dedupe in `handleAdded`)
          // REPLACES the optimistic entry instead of appending a duplicate.
          chatDocumentId: documentId,
          // Thread the resolved peer's public JWK down so the upload hop can
          // defensively (re-)derive the right peer key before the binary
          // encrypt step runs (chat image / thumbnail / video-thumbnail upload
          // as ciphertext). Read through `peerUserRef` so this memoized
          // callback always sees the post-resolution peer (it closes over the
          // first-render `null`); covers the retry path too, which re-invokes
          // this same hop. `null` when the peer hasn't resolved (the upload
          // then relies on the chat-load-derived singleton key, `:756`).
          peerPublicJwk: peerUserRef.current?.publicJwk ?? null,
        });

        if (uploaded) {
          // Success: the persisted doc (carrying the real thumbnail / server
          // paths and `isUploading: true` until transcode completes) echoes
          // back via the live listener and replaces the optimistic tile. Drop
          // the cached `File` — no retry is possible (or needed) anymore.
          sendMediaFilesRef.current.delete(documentId);
        } else {
          // Upload failed. Flip the optimistic tile to the failed-static
          // state: clearing `isUploading` stops the "Processing" label, and
          // `success: false` renders the existing `error.png` read-receipt
          // affordance (type-agnostic — `getReadReceiptMode` returns
          // `failed`). Keep the cached `File` so a tap can re-upload.
          setMessages((prev) =>
            prev.map((m) =>
              m.documentId === documentId
                ? { ...m, success: false, isUploading: false }
                : m,
            ),
          );
        }
        return uploaded;
      } catch (error) {
        appLogger.error('sendMedia upload failed', 'useChat.runMediaSendForId', {
          error: error as Error,
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.documentId === documentId
              ? { ...m, success: false, isUploading: false }
              : m,
          ),
        );
        return false;
      }
    },
    [sendMediaChatMessageUseCase],
  );

  /**
   * Send a media (image / video) message with an optimistic "Processing" tile.
   * Deliberate divergence from Flutter (see the `UseChatResult.sendMedia`
   * JSDoc). Mirrors `sendText`'s optimistic-insert pattern: build the entity
   * client-side with `isUploading: true`, insert at index 0, bump the
   * scroll-to-bottom token, then run the upload+persist through
   * `runMediaSendForId` with the pre-generated `documentId`.
   *
   * @returns the upload-success boolean (from `SendMediaChatMessageUseCase`),
   *   so the AddContent sheet's submit hook can surface the upload-error toast
   *   on `false` — the per-tile `error.png` affordance is already handled here.
   *   Guard-failure early returns (no current user / no `groupChatId`) resolve
   *   `false` (the send did not go through).
   */
  const sendMedia = useCallback(
    async (args: SendChatMediaArgs): Promise<boolean> => {
      if (!currentUser) return false;
      const info = chatUsersInfoRef.current;
      if (!info?.groupChatId) return false;

      const documentId = generateUuidV1();
      const optimistic: ChatMessageEntity = {
        documentId,
        chatId: info.groupChatId,
        idFrom: currentUser.firebaseId,
        idTo: info.peerId,
        timestamp: Date.now(),
        // `MediaType.IMAGE` (0) → image; otherwise video. Same mapping the use
        // case uses (`sendMediaChatMessageUseCase.ts:111`).
        type:
          args.mediaType === MediaType.IMAGE
            ? ChatMessageType.image
            : ChatMessageType.video,
        aspectRatio: args.aspectRatio,
        seenByPeer: false,
        // `isUploading: true` is all the media bubble needs to render the
        // "Processing" placeholder (no server thumbnail yet).
        isUploading: true,
        users: [currentUser.firebaseId, info.peerId],
        content: '',
        success: true,
        giftInstances: 1,
      };

      // Cache the `File` keyed by the optimistic id so a failed tile can be
      // re-uploaded (the original `File` is gone once the sheet closes).
      sendMediaFilesRef.current.set(documentId, args);

      // Optimistic insert at index 0 + sort, exactly like `sendText`.
      setMessages((prev) => {
        const next = [optimistic, ...prev];
        next.sort(ChatUtils.sortByTimestampDesc);
        return next;
      });
      setOpenTimestampForMessageId((prev) => ({
        ...prev,
        [optimistic.timestamp]: false,
      }));
      // Snap the viewport to the bottom (own send), mirroring `sendText`.
      setScrollToBottomToken((prev) => prev + 1);

      return runMediaSendForId(documentId, args, info);
    },
    [currentUser, runMediaSendForId],
  );

  // --- retryMessage / retryAllFailed ---

  /**
   * Re-send a single failed text message. Same idempotency contract as the
   * batch variant: the data-layer `set(...)` is keyed on the message's
   * `documentId`, so sending the SAME optimistic entry does not double-write.
   *
   * IMPORTANT: We pass `{ ...message, success: true }` to the use case rather
   * than the original failed entity. The remapper carries `success` straight
   * through to Firestore (`chatMessageRemapper.ts:251`), so writing the
   * original `success: false` would persist the failure flag on the doc.
   * When the listener's `added` echo arrives, the documentId-dedupe path
   * inside `handleAdded` overwrites the optimistic entry with the echoed
   * doc — which would clobber the just-flipped local `success: true`,
   * leaving the error icon stuck on screen until a second retry tap. This
   * was the "needs two taps to clear" bug from Round 3 / Finding 8.
   *
   * The local flip to `success: true` happens immediately after a successful
   * use-case call; the echoed doc now also carries `success: true`, so the
   * dedupe replacement is a no-op visually.
   *
   * Media branch (Finding 3): a failed image / video tile is retried by
   * re-running the upload+persist for the SAME `documentId` (the cached `File`
   * is keyed on the optimistic id in `sendMediaFilesRef`). The tile is flipped
   * back to the in-flight state (`isUploading: true, success: true`) so the
   * "Processing" placeholder + error-icon-cleared state show during the
   * re-upload. If the cached `File` is gone (e.g. a reload happened — the
   * optimistic insert is in-memory only), the tile is REMOVED instead so it
   * never silently lingers as un-actionable. Any other message type is a no-op.
   */
  const retryMessage = useCallback(
    async (message: ChatMessageEntity): Promise<void> => {
      if (message.success) return;

      // Media retry: re-upload the cached `File` for this tile, or remove the
      // tile if the `File` is no longer available.
      if (
        message.type === ChatMessageType.image ||
        message.type === ChatMessageType.video
      ) {
        const info = chatUsersInfoRef.current;
        if (info === null) return;
        const cachedMedia = sendMediaFilesRef.current.get(message.documentId);
        if (cachedMedia === undefined) {
          // No `File` to re-upload (in-memory cache lost — e.g. after reload).
          // Remove the failed tile so it never silently lingers; the user can
          // re-send from the composer. Mirrors the finding's minimum-viable
          // "removable rather than silently disappearing" affordance.
          setMessages((prev) =>
            prev.filter((m) => m.documentId !== message.documentId),
          );
          return;
        }
        // Flip the tile back to the in-flight state so the "Processing" label
        // returns and the error icon clears while the re-upload runs.
        setMessages((prev) =>
          prev.map((m) =>
            m.documentId === message.documentId
              ? { ...m, success: true, isUploading: true }
              : m,
          ),
        );
        await runMediaSendForId(message.documentId, cachedMedia, info);
        return;
      }

      if (message.type !== ChatMessageType.text) return;
      const info = chatUsersInfoRef.current;
      if (info === null) return;
      try {
        // Persist `success: true` to Firestore so the echo back through the
        // live listener does not re-stamp this bubble as failed (see JSDoc
        // above).
        const cleanMessage: ChatMessageEntity = { ...message, success: true };
        const success = await sendTextChatMessageUseCase.execute({
          chatMessage: cleanMessage,
          chatUsersInfo: info,
        });
        if (success) {
          setMessages((prev) =>
            prev.map((existing) =>
              existing.documentId === message.documentId
                ? { ...existing, success: true }
                : existing,
            ),
          );
        }
      } catch (error) {
        appLogger.error('retryMessage failed', 'useChat.retryMessage', {
          error: error as Error,
        });
      }
    },
    [sendTextChatMessageUseCase, runMediaSendForId],
  );

  /**
   * Walk the list in reverse (oldest first) and re-send every failed text
   * message. Mirrors `chat_screen.dart:626-644` — the reverse walk preserves
   * chronological retry order. Used by `useChatRetryOnReconnect`, which has
   * no per-message context. Per-bubble retry should use `retryMessage`.
   */
  const retryAllFailed = useCallback(async (): Promise<void> => {
    const snapshot = messagesRef.current;
    for (let i = snapshot.length - 1; i >= 0; i--) {
      const m = snapshot[i];
      if (!m.success && m.type === ChatMessageType.text) {
        await retryMessage(m);
      }
    }
  }, [retryMessage]);

  // --- onScrolledToBottom ---

  /**
   * Called when the user scrolls to the bottom of the (reversed) list,
   * i.e. is viewing the newest message. Mirrors `chat_screen.dart:340-356`:
   *  1. Mark `isAtBottomRef = true`.
   *  2. Flush `unreadItems` into the main list, sort, clear.
   *  3. If the most recent message is a peer message, mark it seen.
   */
  const onScrolledToBottom = useCallback((): void => {
    isAtBottomRef.current = true;
    setIsScrolledUp(false);

    // Tear down the transient unread-pill state — the user is back at the
    // bottom, so the pill (and its pending 3 s auto-hide) is no longer
    // relevant. Mirrors Flutter's `_unreadMessageReceived` going back to
    // `false` implicitly when the user re-engages the bottom of the list.
    if (unreadBannerTimerRef.current !== null) {
      window.clearTimeout(unreadBannerTimerRef.current);
      unreadBannerTimerRef.current = null;
    }
    setUnreadBannerVisible(false);

    const pendingUnread = unreadItemsRef.current;
    if (pendingUnread.length > 0) {
      setMessages((prev) => {
        // Flutter `_listItems.insertAll(0, _unreadListItems.reversed)` —
        // semantically a merge that re-sorts by timestamp; concat + sort
        // produces the same final order.
        const next = prev.concat(pendingUnread);
        next.sort(ChatUtils.sortByTimestampDesc);
        return next;
      });
      setUnreadItems([]);
    }

    // Mark-seen on the latest peer message — Flutter `_lastUpdatedChatMessage`
    // logic at `chat_screen.dart:348-356`. We compute "latest peer message"
    // freshly from the merged list so we don't re-issue a transaction for an
    // already-seen one (the use case's `seenByPeer === true` guard handles
    // the redundant-write case but we still want to pick the correct doc).
    const info = chatUsersInfoRef.current;
    if (!info?.groupChatId) return;
    const merged =
      pendingUnread.length > 0
        ? messagesRef.current.concat(pendingUnread).sort(ChatUtils.sortByTimestampDesc)
        : messagesRef.current;
    const latestPeerMsg = merged.find(
      (m) => m.idFrom !== info.currentFirebaseId,
    );
    if (latestPeerMsg !== undefined) {
      void markPeerMessageSeenUseCase
        .execute(info.groupChatId, latestPeerMsg)
        .catch((error: unknown) => {
          appLogger.error(
            'markPeerMessageSeen (scrolled-to-bottom) failed',
            'useChat.onScrolledToBottom',
            { error: error as Error },
          );
        });
    }
  }, [markPeerMessageSeenUseCase]);

  // --- onScrolledAway ---
  //
  // The inverse transition — the list owner calls this when the user
  // scrolls away from the bottom. We need this as an explicit callback (not
  // an inference from `unreadItems.length`) because the FIRST peer message
  // arriving after the user scrolls away must already see
  // `isAtBottomRef.current === false`. Mirrors Flutter's continuous-scroll
  // behavior (`chat_screen.dart:547-579`) where `_currentScroll` is updated
  // on every scroll event.
  const onScrolledAway = useCallback((): void => {
    isAtBottomRef.current = false;
    setIsScrolledUp(true);
  }, []);

  // --- toggleTimestamp ---

  /**
   * Tap-to-toggle the timestamp adornment for the given message. Forces
   * every other entry in the map to `false` ("only one open at a time")
   * then flips the tapped one. Mirrors `chat_screen.dart:1405-1413`.
   */
  const toggleTimestamp = useCallback((message: ChatMessageEntity): void => {
    setOpenTimestampForMessageId((prev) => {
      const next: Record<number, boolean> = {};
      for (const key of Object.keys(prev)) {
        const numKey = Number(key);
        next[numKey] = false;
      }
      const currentValue = prev[message.timestamp] ?? false;
      next[message.timestamp] = !currentValue;
      return next;
    });
  }, []);

  // --- setConversationBlocked ---

  /**
   * Imperative conversation-blocked flip. Called by the chat-actions sheet on
   * a successful block/unblock so the composer + app bar re-render live (the
   * cold value is read once in `runInitialLoad` and otherwise never refreshed).
   * Mirrors Flutter's screen-local `setState` booleans
   * (`chat_screen.dart:86-87`) re-read on `didPopNext`. Stable reference so the
   * page's `handleMore` dependency array stays settled.
   */
  const setConversationBlocked = useCallback((block: boolean): void => {
    setIsConversationBlocked(block);
  }, []);

  // --- Page lifecycle callbacks (Finding 31) ---
  //
  // `useBasePage` is page-only per `presentation.md`. The host page wires
  // its own `useBasePage` and forwards each phase into the callbacks below.
  // Tab-visibility stays inside `useChatPresence`; only the route-axis
  // events move to the page.

  const onPageInit = useCallback((): void => {
    // Idempotent guard — `useBasePage.onInit` only fires once on mount,
    // but the guard makes the intent obvious (and protects against a
    // possible double-mount under StrictMode).
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    // The initial presence dispatch is gated on peer resolution inside
    // `runInitialLoad` to mirror Flutter `_setUserChattingWithPeer`'s
    // `_peerUser != null` guard (`chat_screen.dart:313-321`).
    void runInitialLoad();
  }, [runInitialLoad]);

  const onPageVisible = useCallback((): void => {
    // `useBasePage.onPopNext` — returning to this route from a pushed-on-
    // top route. Flutter `didPopNext`. Re-claim presence (peer is already
    // resolved by now).
    claimPresence();
  }, [claimPresence]);

  const onPageHidden = useCallback((): void => {
    // `useBasePage.onPushNext` / `onPop` — either a new route pushed on top
    // (`didPushNext`) or this route being popped (`didPop`). Same dispatch
    // in both cases.
    releasePresence();
  }, [releasePresence]);

  const onPageDispose = useCallback((): void => {
    // Final teardown: release presence + tear down the live listener.
    // The `releasePresence()` here is defensive — `onPageHidden` should
    // have run first, but `onDispose` covers the path where the page
    // unmounts without a back-nav (e.g. route replacement, KeepAlive
    // eviction).
    releasePresence();
    if (liveUnsubscribeRef.current) {
      liveUnsubscribeRef.current();
      liveUnsubscribeRef.current = null;
    }
    if (sendErrorTimerRef.current !== null) {
      window.clearTimeout(sendErrorTimerRef.current);
      sendErrorTimerRef.current = null;
    }
    // Cancel the transient unread-pill auto-hide timer if one is pending —
    // otherwise it would fire post-dispose into a stale React tree.
    if (unreadBannerTimerRef.current !== null) {
      window.clearTimeout(unreadBannerTimerRef.current);
      unreadBannerTimerRef.current = null;
    }
  }, [releasePresence]);

  // --- Retry on reconnect (both transitions) ---
  // Mirrors `chat_screen.dart:626-644` exactly — `connectionActive=true`
  // re-claims presence + retries; `connectionActive=false` releases
  // presence.
  useChatRetryOnReconnect({
    isOnline,
    claimPresence,
    releasePresence,
    retryAllFailed,
  });

  return {
    chatUsersInfo,
    messages,
    peerUser,
    isFirstLoad,
    isFetchingMore,
    hasMore,
    isSubscribedMutual,
    isConversationBlocked: conversationBlocked,
    setConversationBlocked,
    currentUserBlocked,
    currentUserBlockedByPeer,
    peerUserBlocked,
    isOnline,
    sendError,
    openTimestampForMessageId,
    unreadCount: unreadItems.length,
    unreadBannerVisible,
    isScrolledUp,
    unreadItems,
    myLastMessageReadPosition,
    scrollToBottomToken,
    sendText,
    sendMedia,
    retryMessage,
    retryAllFailed,
    onScrolledToBottom,
    onScrolledAway,
    onLoadMore,
    toggleTimestamp,
    onPageInit,
    onPageVisible,
    onPageHidden,
    onPageDispose,
  };
};
